import React, { useState, useEffect } from 'react';
import { Paper, Typography, Box, Button, Avatar, Chip, CircularProgress, alpha } from 'components/mui';
import { DownloadIcon } from 'components/icons';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import AppleIcon from '@mui/icons-material/Apple';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SecurityIcon from '@mui/icons-material/Security';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';
import axiosInstance from 'api/axiosInstance';
import logo from '../../assets/images/stream.png';

/** Map icon_name strings from the DB to MUI icon components */
const ICON_MAP = {
  PhoneAndroid: PhoneAndroidIcon,
  Apple: AppleIcon,
  DeveloperMode: DeveloperModeIcon,
  InsertDriveFile: InsertDriveFileIcon,
  Security: SecurityIcon,
  DesktopWindows: DesktopWindowsIcon,
};

function Downloads() {
  const { notifySuccess, notifyError } = useNotificationProvider();
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDownloads = async () => {
      try {
        const { data } = await axiosInstance.get('/api3.2/downloads');
        setDownloads(data || []);
      } catch (error) {
        console.error('[Downloads] fetchDownloads:', error);
        notifyError('Failed to load downloads');
      } finally {
        setLoading(false);
      }
    };
    fetchDownloads();
  }, [notifyError]);

  const handleDownload = (item) => {
    notifySuccess(`Download started for ${item.display_name}`);
    window.open(`/api3.2/downloads/${item.download_key}`, '_blank');
  };

  if (loading) {
    return (
      <Box sx={{ p: 2, maxWidth: 800, display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 800 }}>
      <Typography variant="h4" sx={{ mb: 0.5 }}>
        Downloads
      </Typography>
      <Typography variant="h6" color="text.secondary" sx={{ mb: 3, fontWeight: 400 }}>
        Mobile apps and tools available for download.
      </Typography>

      {downloads.length === 0 ? (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
          No downloads available at this time.
        </Typography>
      ) : (
        downloads.map((item) => {
          const Icon = ICON_MAP[item.icon_name] || InsertDriveFileIcon;
          const color = item.icon_color || 'primary';
          return (
            <Paper key={item.id} elevation={1} sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                }}
              >
                <Avatar
                  sx={(theme) => ({
                    bgcolor: alpha(theme.palette[color]?.main || theme.palette.primary.main, 0.1),
                    color: `${color}.main`,
                    width: 48,
                    height: 48,
                    flexShrink: 0,
                  })}
                >
                  <Icon />
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {item.display_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {item.description}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {item.version && <Chip label={item.version} size="small" variant="outlined" />}
                    {item.file_size_display && <Chip label={item.file_size_display} size="small" variant="outlined" />}
                  </Box>
                </Box>

                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownload(item)}
                  sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}
                >
                  Download
                </Button>
              </Box>
            </Paper>
          );
        })
      )}

      <Chip
        label="Powered by Stream Data Systems"
        variant="outlined"
        sx={{
          position: 'fixed',
          bottom: 10,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 99,
        }}
        icon={
          <Box
            component="img"
            src={logo}
            alt="logo"
            sx={{ width: 20, height: 20 }}
          />
        }
      />
    </Box>
  );
}

export default Downloads;
